/* eslint-disable class-methods-use-this */
import io from 'socket.io-client'
import qs from 'query-string'

import { SecurityUtils } from '../../utils'

const EVENT_NAME_GENERIC = 'event'
const EVENT_NAME_CONNECTIONS = 'connections'

/**
 * Class to handle socket connection
 */
class Socket {
    static CallbacksKeys = {
        CONNECT: 'CONNECT',
        CONNECTED: 'CONNECTED',
        DEVICE_CONNECTION: 'DEVICE_CONNECTION',
        DEVICE_DISCONNECTION: 'DEVICE_DISCONNECTION',
        DEVICE_DOWNLOAD_PERCENTAGE: 'DEVICE_DOWNLOAD_PERCENTAGE',
        DEVICE_FEEDBACK: 'DEVICE_FEEDBACK',
        DEVICE_FETCH_CONNECTIONS: 'DEVICE_FETCH_CONNECTIONS',
        DEVICE_SCREENSHOT: 'DEVICE_SCREENSHOT',
        DISCONNECT: 'DISCONNECT',
        DISCONNECTED: 'DISCONNECTED',
        ERROR: 'ERROR',
        DEVICE_OPEN_STATUS_PANEL: 'DEVICE_OPEN_STATUS_PANEL',
        DEVICE_CLEAR_CACHE: 'DEVICE_CLEAR_CACHE',
        DEVICE_CLEAR_CONTENT: 'DEVICE_CLEAR_CONTENT',
    }

    /**
     *
     * @param {Object} callbacks
     */
    constructor(callbacks = {}) {
        this.started = false

        this.connected = false

        this.id = null

        this.setup = false

        this.logger = console

        this.callbacks = callbacks
    }

    /**
     * Connect to socker server
     * @param {String} url
     * @param {String} sessionID
     */
    connect(url, sessionID) {
        if (!url) {
            return
        }

        this.getCallback(Socket.CallbacksKeys.CONNECT)()

        if (this.service) {
            this.close()
        }

        this.id = sessionID
        const fullUrl = qs.stringifyUrl({
            url,
            query: {
                uid: this.id,
                //info: '1',
            },
        })

        try {
            this.service = io(fullUrl, {
                transports: ['websocket'],
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 10000,
                reconnectionDelayMax: 5000,
            })

            this.setupEvents()
        } catch (error) {
            this.logger.error(error)
        }

        return this
    }

    /**
     * Get callback
     * @param {String} key
     */
    getCallback(key) {
        return this.callbacks[key] || (() => null)
    }

    /**
     * Setup socket events
     */
    setupEvents() {
        if (!this.setup) {
            this.service.on('connect', this.onConnection.bind(this))
            this.service.on('connect_error', this.onError.bind(this))
            this.service.on('connect_timeout', this.onError.bind(this))
            this.service.on('disconnect', this.onDisconnect.bind(this))
            this.service.on(EVENT_NAME_GENERIC, this.onEvent.bind(this))
        }

        this.setup = true
    }

    /**
     * Close connections
     */
    close() {
        if (!this.service) return null

        if (this.connected) {
            this.service.close()
            this.connected = false
        }

        this.service.io.reconnection(false)
        this.service.io.cleanup()

        this.getCallback(Socket.CallbacksKeys.DISCONNECT)()
    }

    /**
     * On Event connection
     */
    onConnection() {
        this.service.emit('authentication', SecurityUtils.socketAuthObject(JSON.parse('{}')))

        try {
            this.connected = true
            this.getCallback(Socket.CallbacksKeys.CONNECTED)()
        } catch (error) {
            this.logger.error(error)
        }
    }

    /**
     * On Error
     * @param {Error} error
     */
    onError(error) {
        this.logger.error('connection timeout', error)
        this.getCallback(Socket.CallbacksKeys.ERROR)()
    }

    /**
     * On event called
     * @param {Object} payload
     */
    onEvent(payload = {}) {
        const { uid, action } = payload

        // actions without `action` field
        if (!action) {
            return this.onAltEvent(payload)
        }

        // Do nothing if the uid belongs to an user session
        if (typeof uid === 'string' && uid.startsWith('vol_sso_')) {
            return null
        }

        switch (action) {
            case 'connection':
                if (uid) {
                    this.getCallback(Socket.CallbacksKeys.DEVICE_CONNECTION)(uid)
                }
                break
            case 'disconnection':
                if (uid) {
                    this.getCallback(Socket.CallbacksKeys.DEVICE_DISCONNECTION)(uid)
                }
                break
            default:
        }
    }

    /**
     * On event called (alternate version)
     * @param {Object} payload
     */
    onAltEvent(payload = {}) {
        const { data } = payload

        if (!data) return null

        const { 'device-id': uid, deviceId, message, groupId } = data

        const device = uid || deviceId

        if (!device) return null

        if (message) {
            return this.getCallback(Socket.CallbacksKeys.DEVICE_FEEDBACK)(device, message, `${groupId}#${message}`)
        }
        const { 'download-percentage': downPercValue, screenshot, 'open-status-panel': openStatusPanel, clearDeviceCache, clearDeviceContent } = data

        // Download percentage action
        if (downPercValue) {
            this.getCallback(Socket.CallbacksKeys.DEVICE_DOWNLOAD_PERCENTAGE)(device, downPercValue)
        }

        // Screenshot action
        if (screenshot) {
            this.getCallback(Socket.CallbacksKeys.DEVICE_SCREENSHOT)(uid, screenshot.replace(/\s/g, ''))
        }

        //Open Status Panel action
        if (openStatusPanel) {
            this.getCallback(Socket.CallbacksKeys.DEVICE_OPEN_STATUS_PANEL)(uid)
        }

        if (clearDeviceCache) {
            this.getCallback(Socket.CallbacksKeys.DEVICE_CLEAR_CACHE)(uid)
        }
        if (clearDeviceContent) {
            this.getCallback(Socket.CallbacksKeys.DEVICE_CLEAR_CONTENT)(uid)
        }
    }

    /**
     * On event disconnect
     */
    onDisconnect() {
        this.connected = false
        this.getCallback(Socket.CallbacksKeys.DISCONNECTED)()
    }

    /**
     * Fetch connected devices
     */
    fetchConnectedDevices() {
        const saveDevices = (data) => {
            if (!data || !(data instanceof Object)) {
                return
            }

            const devices = Object.keys(data).map((k) => k.replace('device', ''))
            this.getCallback(Socket.CallbacksKeys.DEVICE_FETCH_CONNECTIONS)(devices)
        }

        this.service.emit(EVENT_NAME_CONNECTIONS, saveDevices)
    }

    /**
     * Reboot device
     * @param {String} uid Device id
     * @param {String} groupId Group id
     */
    rebootDevice(uid, groupId) {
        if (!uid) {
            return
        }

        const data = {
            command: 'reboot',
            replyTo: this.id,
        }

        if (groupId) {
            data.groupId = groupId
        }

        this.service.emit('event', {
            uid,
            data,
        })
    }

    /**
     * Update device content
     * @param {String} uid Device id
     * @param {String} groupId Group id
     */
    updateContent(uid, groupId) {
        if (!uid) {
            return
        }

        const data = {
            command: 'content-update',
            replyTo: this.id,
        }

        if (groupId) {
            data.groupId = groupId
        }

        this.service.emit('event', {
            uid,
            data,
        })
    }

    /**
     * Update device settings
     * @param {String} uid Device id
     * @param {String} groupId Group id
     */
    updateSettings(uid, groupId) {
        if (!uid) {
            return
        }

        const data = {
            command: 'settings-update',
            replyTo: this.id,
        }

        if (groupId) {
            data.groupId = groupId
        }

        this.service.emit('event', {
            uid,
            data,
        })
    }

    /**
     * Demand device screenshot
     * @param {String} uid Device id
     * @param {String} groupId Group id
     */
    demandScreenshot(uid, groupId) {
        if (!uid) {
            return
        }

        const data = {
            command: 'screenshot',
            replyTo: this.id,
        }

        if (groupId) {
            data.groupId = groupId
        }

        this.service.emit('event', {
            uid,
            data,
        })
    }

    /**
     * Demand device open status panel
     * @param {String} uid Device id
     * @param {String} groupId Group id
     */
    openStatusPanel(uid) {
        if (!uid) {
            return
        }

        const data = {
            command: 'open-status-panel',
            replyTo: this.id,
        }

        this.service.emit('event', {
            uid,
            data,
        })
    }

    /**
     * Demand the device clears it cache
     * @param {String} uid Device id
     */
    clearDeviceCache(uid) {
        if (!uid) {
            return
        }

        const data = {
            command: 'clear-device-cache',
            replyTo: this.id,
        }

        this.service.emit('event', {
            uid,
            data,
        })
    }

    /**
     * Demand a device clear all its content
     * @param {String} uid Device id 
     * @returns 
     */
    clearDeviceContent(uid) {
        if (!uid) {
            return
        }

        const data = {
            command: 'clear-device-content',
            replyTo: this.id,
        }

        this.service.emit('event', {
            uid,
            data,
        })
    }


    /**
     * Restart app
     * @param {String} uid Device id
     * @param {String} groupId Group id
     */
    restartApp(uid, groupId) {
        if (!uid) {
            return
        }

        const data = {
            command: 'restart',
            replyTo: this.id,
        }

        if (groupId) {
            data.groupId = groupId
        }

        this.service.emit('event', {
            uid,
            data,
        })
    }

    /**
     * Promo reload
     * @param {String} uid Device id
     * @param {String} groupId Group id
     */
    reloadPromo(uid, groupId) {
        if (!uid) {
            return
        }

        const data = {
            command: 'layer-data',
            replyTo: this.id,
            data: {
                actionType: 'promo-reload',
                actionDetails: undefined,
            },
        }

        if (groupId) {
            data.groupId = groupId
        }

        this.service.emit('event', {
            uid,
            data,
        })
    }
}

export default Socket
